// apiError — shared mapper from axios errors to user-meaningful copy.
//
// Backstory: every modal used to collapse non-handled error codes into a
// generic "try again" string, so an operator could not tell apart a 422
// validation error, a 503 provider outage, and a 500 internal bug. The third
// audit pass flagged this in five places (B1, B2, B3, B4, I2). This module
// centralises the mapping so every catch block can do
//
//   setError(apiErrorMessage(err, "Could not create the merchant."))
//
// and the copy stays consistent across pages.
//
// The mapping has two layers:
//   1. Known backend `code` strings → curated user-facing copy.
//   2. HTTP status fallback (404/409/422/429/5xx) for unknown codes.
//   3. The caller-supplied fallback string for anything else (network drop,
//      timeout, CORS preflight failure, etc.).
//
// Backend codes are stable per CLAUDE.md / handlers/*.go — extend this dict
// when a new code lands rather than re-implementing the switch per modal.
import axios, { type AxiosError } from "axios";

import { MalformedJsonResponseError } from "@/api/client";

/**
 * Curated copy for known backend `code` values returned in the
 * `{ error, code }` envelope. Keep in sync with internal/handlers/*.go and
 * internal/service/payout/service.go sentinel errors.
 */
const ERROR_COPY: Record<string, string> = {
  // Auth / RBAC
  no_claims: "Your session expired. Sign in again.",
  role_required: "You don't have permission for this action.",
  merchant_forbidden: "You don't have access to this merchant.",
  membership_required: "Your membership on this merchant was removed.",
  membership_role_insufficient:
    "This action requires a higher role on this merchant.",
  token_signature_invalid: "Your session is invalid. Sign in again.",
  token_expired: "Your session expired. Sign in again.",

  // Membership management (Rbac.tsx)
  membership_exists: "That user already has a membership on this merchant.",
  membership_not_found: "That membership no longer exists.",
  last_owner: "This is the last owner — promote another member to owner first.",
  role_invalid: "Pick a valid role (owner, operator, or viewer).",
  user_id_invalid: "Enter a valid auth-service user ID.",

  // Merchant create / update (Merchants.tsx, MerchantDetail.tsx)
  slug_taken: "That slug is already in use — pick another.",
  slug_invalid:
    "Slug must be 2–40 chars: lowercase letters, digits and hyphens.",
  display_name_invalid: "Display name can't be empty.",
  merchant_not_found: "Merchant not found.",

  // User lookup / invite (users.ts, AddMemberModal in Rbac.tsx).
  // AUDIT-PASS-6 critical-tech-lead Findings #3 + #4: the
  // bearer-forwarded calls to auth-service produce status-passthrough
  // codes that need actionable copy, not the generic "Conflict" /
  // "Server error" fallbacks. The codes correspond to specific ops
  // problems an operator can act on without paging the engineering
  // team:
  //   - 400 from auth-service: missing tenant scope on the operator's
  //     grant (UpsertGrant rejects non-platform-admin callers without
  //     attributes.distributor_id). Tell ops to bind the scope.
  //   - 403: operator lacks payouts:users:read / payouts:grants:write.
  //     auth-admin scaffold only seeds these on payouts/admin role.
  //   - unreachable: transport failure, retry-or-page situation.
  // AUDIT-PASS-6 deferred-#1 closed: distributor_id is now forwarded
  // automatically from the operator's JWT claims (see users.go). A 400
  // from auth-service should be rare and the copy points at the rare
  // causes (operator's grant has NO distributor_id, or attribute schema
  // changed). It no longer reads "ask ops to bind distributor_id"
  // because that's no longer the most common cause.
  auth_service_status_400:
    "auth-service rejected the invite. Most likely cause: your grant on auth-service has no distributor_id or the attribute schema changed — ask ops.",
  auth_service_status_403:
    "You don't have the auth-service permissions to look up users or invite. Ask the platform admin to grant `payouts:users:read` and `payouts:grants:write` to your role.",
  auth_service_status_404:
    "auth-service couldn't process the request — the audience may be misconfigured.",
  auth_service_unreachable:
    "Can't reach auth-service right now. Try again, or page ops if it persists.",
  user_not_found:
    "No payouts user with that email yet — they need to sign in once via OTP first, then you can add them.",
  email_invalid: "That doesn't look like an email address.",
  no_bearer: "Your session expired. Sign in again.",

  // AUDIT-PASS-6 audit L-5: backend internals that occasionally bubble
  // up to the panel — surface a friendly hint rather than the bare
  // status fallback. These fire on rare error paths (the merchant
  // context is missing from ctx, or a DB write failed mid-tx) but the
  // operator should still see something more actionable than
  // "Server error" with the raw code in parens.
  merchant_id_missing:
    "No active merchant — pick one in the top bar and try again.",
  membership_check:
    "Couldn't verify the membership state. Try again, or page ops if it persists.",
  membership_create:
    "Couldn't save the membership. Try again, or page ops if it persists.",

  // Payouts
  beneficiary_not_found: "Beneficiary not found.",
  beneficiary_not_registered:
    "That beneficiary is not registered with the provider yet — open the merchant detail page and register them first.",
  provider_credential_not_found:
    "The selected rail has no bank credentials — add them on the Bank rails screen.",
  payout_not_found: "Payout not found.",
  // Whole-rupees guard (backend 2747b54). The panel's amount input already
  // rejects decimals, but the M2M API and CSV upload paths still need
  // copy for callers that slip a fractional value through.
  amount_not_whole_rupees:
    "Amount must be a whole rupee value — IMPS and NEFT don't settle paise.",
  // IDFC per-txn band guard (backend 7725914). The panel mirrors the
  // band inline in the new-payout modal and the CSV validator, but the
  // M2M API path still needs copy for callers that slip an out-of-band
  // amount through. Bounds: ₹300 ≤ amount ≤ ₹1,00,000.
  amount_out_of_range:
    "Amount must be between ₹300 and ₹1,00,000 per payout (per-transaction band).",

  // Batch upload
  request_id_conflict:
    "A request ID in this batch is already in use by an earlier submission. Use fresh request IDs and re-upload.",
  duplicate_request_id_in_batch:
    "Two rows in your CSV share the same request ID. Fix the highlighted rows and re-upload.",
  batch_too_large:
    "This batch exceeds the 500-row server limit. Split the file and try again.",
  batch_request_too_large:
    "The batch payload is larger than the server accepts. Split the file into smaller batches and try again.",
  batch_not_found: "Batch not found.",
  invalid_bank_account:
    "That bank rail is not available on this merchant. Pick a different source account on Step 1.",

  // Merchant status gate (PDF round 2). Server returns these from
  // Initiate/BatchInitiate/Approve/BatchApprove when merchants.status is
  // 'suspended' or 'closed'. Banner at the top of the shell shows the
  // primary notice; this copy is the inline echo at the form/submit site.
  merchant_suspended:
    "This merchant is suspended — payouts and approvals are temporarily blocked.",
  merchant_closed:
    "This merchant is closed — payouts and approvals are blocked. Contact platform admin.",

  // Customer duplicate (PDF round 2 item 2). Caller-friendly fallback;
  // the structured response body carries existing_customer_id + value so
  // callers that want a precise message (e.g. AddBeneficiaryModal) can
  // build a richer alert via extractCustomerDuplicate below.
  customer_duplicate_email:
    "This email is already attached to a different customer on this merchant. Reconcile the existing customer record before retrying.",
  customer_duplicate_phone:
    "This phone is already attached to a different customer on this merchant. Reconcile the existing customer record before retrying.",

  // API keys
  scopes_invalid: "One of the requested scopes is not allowed.",
  key_not_found: "API key not found.",
  key_already_revoked: "That key was already revoked.",

  // Bank accounts / credentials
  bank_account_not_found: "Bank rail not found.",
  credential_not_found: "Credentials not found for this rail.",
  credentials_invalid:
    "The bank credentials were rejected. Check email, password, and merchant key.",

  // Rate limit
  rate_limited:
    "Too many requests — slow down and try again in a few seconds.",

  // Validation
  invalid_request: "The request was rejected as invalid.",
};

/**
 * Best-effort extraction of the backend `code` field from an axios error.
 * Returns `undefined` for non-axios errors and for axios errors without a
 * structured body (network drop, CORS preflight, etc.).
 */
export function extractErrorCode(err: unknown): string | undefined {
  if (!axios.isAxiosError(err)) return undefined;
  const data = (err as AxiosError).response?.data as
    | { code?: unknown }
    | undefined;
  if (data && typeof data.code === "string") return data.code;
  return undefined;
}

/**
 * Best-effort extraction of the `row_indices` array surfaced by structured
 * 422 responses (e.g. `duplicate_request_id_in_batch`). Returns an empty
 * array when the field is missing, malformed, or contains non-integer
 * elements — callers can treat that as "no highlighting".
 *
 * AUDIT-PASS-8 backend H-1 panel pair: the backend's intra-batch dedup
 * surfaces 0-based indices into the SUBMITTED row array. Callers are
 * responsible for translating those back to the CSV's 1-based row numbers
 * via their own valid-row mapping — this helper does NOT do that
 * translation (it can't; the mapping is caller-specific).
 */
export function extractRowIndices(err: unknown): number[] {
  if (!axios.isAxiosError(err)) return [];
  const data = (err as AxiosError).response?.data as
    | { row_indices?: unknown }
    | undefined;
  if (!data || !Array.isArray(data.row_indices)) return [];
  const out: number[] = [];
  for (const v of data.row_indices) {
    if (typeof v === "number" && Number.isInteger(v) && v >= 0) {
      out.push(v);
    }
  }
  return out;
}

/**
 * CustomerDuplicateDetail — extra fields surfaced on customer_duplicate_email
 * or customer_duplicate_phone 422s from beneficiary creation / CSV upload.
 * Carries the existing customer's id + the colliding canonical value
 * (lowercased email, digits-only phone) so the panel can render
 *   'The email "jane@acme.in" is already attached to customer #42.'
 * which is more actionable than the generic copy in ERROR_COPY above.
 */
export interface CustomerDuplicateDetail {
  field: "email" | "phone";
  existing_customer_id: number;
  value: string;
}

/**
 * extractCustomerDuplicate — returns the structured detail when an axios
 * error carries the customer_duplicate_{email,phone} 422 envelope. Returns
 * null otherwise (caller falls back to ERROR_COPY).
 */
export function extractCustomerDuplicate(
  err: unknown,
): CustomerDuplicateDetail | null {
  if (!axios.isAxiosError(err)) return null;
  const data = (err as AxiosError).response?.data as
    | { code?: unknown; existing_customer_id?: unknown; value?: unknown }
    | undefined;
  if (!data || typeof data.code !== "string") return null;
  if (
    data.code !== "customer_duplicate_email" &&
    data.code !== "customer_duplicate_phone"
  ) {
    return null;
  }
  const field: "email" | "phone" =
    data.code === "customer_duplicate_email" ? "email" : "phone";
  const existing =
    typeof data.existing_customer_id === "number"
      ? data.existing_customer_id
      : 0;
  const value = typeof data.value === "string" ? data.value : "";
  return { field, existing_customer_id: existing, value };
}

/**
 * VerifyFailedDetail — extra fields surfaced on the verify_failed 422 returned
 * by POST /v1/merchants/:id/beneficiaries/:bid/verify. Carries the upstream
 * provider classification (provider_code, e.g. "account_not_found",
 * "invalid_hash") and the verbatim provider-emitted message. Either field may
 * be empty (the provider may classify without a message, or vice versa).
 */
export interface VerifyFailedDetail {
  provider_code: string;
  provider_message: string;
}

/**
 * extractVerifyFailedDetail — returns provider_code + provider_message when an
 * axios 422 carries the verify_failed envelope, or null otherwise. The
 * VerifyBeneficiaryModal uses these fields to render the actual upstream
 * reason inline rather than a generic "could not verify."
 */
export function extractVerifyFailedDetail(
  err: unknown,
): VerifyFailedDetail | null {
  if (!axios.isAxiosError(err)) return null;
  const data = (err as AxiosError).response?.data as
    | { code?: unknown; provider_code?: unknown; provider_message?: unknown }
    | undefined;
  if (!data || data.code !== "verify_failed") return null;
  return {
    provider_code:
      typeof data.provider_code === "string" ? data.provider_code : "",
    provider_message:
      typeof data.provider_message === "string" ? data.provider_message : "",
  };
}

/**
 * HTTP-status fallback when no `code` mapping fires. Keep these generic — if
 * a status has a stable code (e.g. 429 → "rate_limited"), the dict above wins
 * and never reaches here.
 */
function statusMessage(status: number, fallback: string): string {
  if (status === 404) return "Resource not found. It may have been deleted.";
  if (status === 409)
    return "Conflict — that resource already exists or was changed by someone else.";
  if (status === 413)
    // AUDIT-PASS-8 H-3: 413 from the proxy/Go body cap arrives with no
    // structured `code` field, so the dictionary path doesn't fire. Pre-fix,
    // this fell through to the caller's generic fallback ("Something went
    // wrong submitting the batch"), giving the operator no actionable signal.
    // The copy here tells them exactly what to do: split and retry.
    return "The request is larger than the server accepts. Split into smaller batches and try again.";
  if (status === 422)
    return "Invalid input — check the values and try again.";
  if (status === 429)
    return "Too many requests — slow down and try again in a few seconds.";
  if (status >= 500 && status < 600)
    return "Server error. Try again, or contact support if it persists.";
  return fallback;
}

/**
 * apiErrorMessage maps an axios error (or any thrown value) to a user-facing
 * string. Always returns a non-empty string — never undefined.
 *
 * Precedence (highest to lowest):
 *   1. Known backend `code` → ERROR_COPY[code]
 *   2. HTTP status (4xx/5xx) → statusMessage(status, fallback)
 *   3. Caller-supplied `fallback`
 *
 * The fallback is shown verbatim for non-axios errors (e.g. a thrown string
 * from a fetch wrapper), for axios timeouts, and for network drops.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const code = extractErrorCode(err);
  if (code && ERROR_COPY[code]) return ERROR_COPY[code];

  // AUDIT-PASS-6 P-1: MalformedJsonResponseError is thrown by the client's
  // transformResponse when a 2xx body fails JSON.parse — invariably because
  // a misconfigured reverse proxy or CDN intercepted the request and served
  // an HTML error page with status 200. Without this branch the error
  // bypasses both the code dict and the status fallback (it's a plain
  // Error, not an AxiosError) and the caller's generic fallback ("Could
  // not load merchants") fires. The operator sees "Could not load
  // merchants. Try again." for the next 6 hours while ops is paging the
  // proxy team. Routable, specific copy here flips them straight to the
  // transport-layer hypothesis.
  if (err instanceof MalformedJsonResponseError) {
    return "The server's response could not be read — a reverse proxy or CDN may be returning HTML instead of JSON. Refresh, and if it keeps happening, alert ops.";
  }

  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    if (status != null) {
      // Surface the raw code (if any) alongside the status copy so support
      // tickets carry the signal even when we don't have curated copy yet.
      const base = statusMessage(status, fallback);
      if (code) return `${base} (${code})`;
      return base;
    }
    // No response → likely network drop or timeout.
    return "Cannot reach the server. Check your connection and try again.";
  }

  return fallback;
}
